import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Target, LogOut, Menu, MessageSquare, BarChart, LayoutDashboard, Users, Image as ImageIcon } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { supabase, forceReconnect } from '../lib/supabase';

const Navbar = () => {
  const { user, subscription, clearAuth } = useAuthStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const menuRef = useRef<HTMLDivElement>(null); // Referência para o menu

  // Efeito para fechar o menu ao clicar fora dele
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    // Adiciona o listener quando o menu está aberto
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Remove o listener quando o componente é desmontado ou o menu fecha
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Seus outros useEffects permanecem aqui...
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible');
      if (document.visibilityState === 'visible') {
        forceReconnect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (isDashboard && isVisible) {
      forceReconnect();
    }
  }, [location.pathname, isVisible, isDashboard]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    
    const forceLogoutTimer = setTimeout(() => {
      console.warn('Logout está demorando muito, forçando limpeza...');
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }, 1000);

    try {
      const { success } = await forceReconnect();
      if (!success) {
        console.warn('Reconnection attempt failed, proceeding with forced logout');
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      clearAuth();
      clearTimeout(forceLogoutTimer);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    } finally {
      setIsLoggingOut(false);
      clearTimeout(forceLogoutTimer);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavigation = (tab: string) => {
    setIsMenuOpen(false);
    navigate(`/dashboard/${tab}`);
  };

  const isSubscriptionExpired = subscription?.next_billing_date 
    ? new Date(subscription.next_billing_date) < new Date() 
    : false;

  const navLinks = [
    {
      name: 'Leads',
      tab: 'leads',
      icon: <Users className="h-5 w-5" />,
    },
    {
      name: 'Segmentação',
      tab: 'targeting',
      icon: <Target className="h-5 w-5" />,
    },    
    {
      name: 'Chat IA',
      tab: 'chat',
      icon: <MessageSquare className="h-5 w-5" />,
      disabled: false
    },
    {
      name: 'Predições',
      tab: 'metrics',
      icon: <BarChart className="h-5 w-5" />,
      disabled: false
    },
    {
      name: 'Posts',
      tab: 'posts',
      icon: <ImageIcon className="h-5 w-5" />,
    }
  ];

  const isTabActive = (tab: string) => {
    if (isDashboard) {
      const currentTab = location.pathname.split('/')[2] || 'targeting';
      return currentTab === tab;
    }
    return false;
  };

  const handleScrollToPricing = () => {
    if (location.pathname === '/') {
      const pricingSection = document.getElementById('pricing-section');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/#pricing-section');
    }
  };

  return (
  <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between h-16">
        <div className="flex items-center">
          <Link to="/" className="flex items-center">
            <Target className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
            <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">Aura AI</span>
          </Link>

          {user && isVisible && (
            <div className="hidden md:flex items-center space-x-4 ml-10">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => handleNavigation(link.tab)}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                    isTabActive(link.tab)
                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-white'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:hover:bg-gray-700 dark:text-gray-300 dark:hover:text-white'
                  }`}
                >
                  {link.icon}
                  <span className="ml-2">{link.name}</span>
                </button>
              ))}

              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                  isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''
                } text-gray-600 dark:text-gray-300 hover:bg-gray-50 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white`}
              >
                {isLoggingOut ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saindo...
                  </>
                ) : (
                  <>
                    <LogOut className="h-5 w-5 mr-2" />
                    Sair
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center">
          {user ? (
            <>
              {isVisible && (
                <div className="md:hidden flex items-center mr-4">
                  <button
                    onClick={() => handleNavigation('targeting')}
                    className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    <LayoutDashboard className="h-6 w-6" />
                  </button>
                </div>
              )}

              <div className="md:hidden flex items-center">
                <button
                  onClick={toggleMenu}
                  className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </div>

              {isMenuOpen && isVisible && (
                <div 
                  ref={menuRef}
                  className="absolute top-16 right-0 w-56 py-2 mt-2 bg-white dark:bg-gray-800 rounded-md shadow-xl z-50 md:hidden border border-gray-200 dark:border-gray-700"
                >
                  {navLinks.map((link) => (
                    <button
                      key={link.name}
                      onClick={() => !link.disabled && handleNavigation(link.tab)}
                      className={`flex items-center w-full px-4 py-2 text-sm ${
                        isTabActive(link.tab)
                          ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {link.icon}
                      <span className="ml-2">{link.name}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    disabled={isLoggingOut}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {isLoggingOut ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saindo...
                      </>
                    ) : (
                      <>
                        <LogOut className="h-5 w-5 mr-2" />
                        Sair
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Entrar
              </Link>
              <button
                onClick={() => window.location.href = 'https://aiaura.com.br/register'}   
                className="ml-4 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              >
                Teste Grátis
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  </nav>
);
};

export default Navbar;